import RenderPipeline from "../renderpipeline";
import ShaderService from "../../shader/shader_service";
import Camera from "../../scene/camera";
import ResourceService from "../../resource/resourceservice";
import Transform from "../transform";
import CollisionService from "../../collision/collision_service";
import { CollisionVolume } from "../../collision/collision_volume";
import { Aabb } from "../../collision/aabb";
import { BoundingSphere } from "../../collision/bounding_sphere";
import { RENDER_SAMPLE_COUNT } from "../render_config";

export default class CollisionRenderPipeline extends RenderPipeline {
  label: string;
  shader_name: string;
  collision_service: CollisionService;

  camera_buffer: GPUBuffer | null = null;
  bind_group: GPUBindGroup | null = null;

  vertex_buffer: GPUBuffer | null = null;
  vertex_capacity_floats = 0;
  vertex_count = 0;

  constructor(key: string, shader_name: string, collision_service: CollisionService) {
    super(key + "_" + "collision");
    this.label = key + "_" + "collision";
    this.shader_name = shader_name;
    this.collision_service = collision_service;
  }

  
  async build(
  device: GPUDevice,
  format: GPUTextureFormat,
  shader_service: ShaderService,
  resource_service: ResourceService
): Promise<void> {
  const shader = shader_service.get_render(this.shader_name);

  this.pipeline = device.createRenderPipeline({
    layout: "auto",
    label: this.label,
    vertex: {
      module: shader,
      entryPoint: "vs_main",
      buffers: [
        {
          arrayStride: 28,
          attributes: [
            { shaderLocation: 0, offset: 0, format: "float32x3" },
            { shaderLocation: 1, offset: 12, format: "float32x4" },
          ],
        },
      ],
    },
    fragment: {
      module: shader,
      entryPoint: "fs_main",
      targets: [
        {
          format,
          blend: {
            color: {
              operation: "add",
              srcFactor: "src-alpha",
              dstFactor: "one-minus-src-alpha",
            },
            alpha: {
              operation: "add",
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
            },
          },
        },
      ],
    },
    primitive: {
      topology: "line-list",
      cullMode: "none",
    },
    depthStencil: {
      format: "depth24plus",
      depthWriteEnabled: false,
      depthCompare: "less",
    },
    multisample: {
      count: RENDER_SAMPLE_COUNT,
    },
  });

  this.camera_buffer = device.createBuffer({
    size: 64,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  this.bind_group = device.createBindGroup({
    layout: this.pipeline.getBindGroupLayout(0),
    label: this.label + "_bind_group",
    entries: [
      { binding: 0, resource: { buffer: this.camera_buffer } },
    ],
  });

  this.ensure_vertex_capacity(device, 4096);
}
  

  private ensure_vertex_capacity(device: GPUDevice, required_float_count: number): void {
    if (this.vertex_buffer && this.vertex_capacity_floats >= required_float_count) {
      return;
    }

    let new_capacity = Math.max(4096, this.vertex_capacity_floats || 4096);
    while (new_capacity < required_float_count) {
      new_capacity *= 2;
    }

    this.vertex_capacity_floats = new_capacity;

    this.vertex_buffer = device.createBuffer({
      label: this.label + "_vertex_buffer",
      size: this.vertex_capacity_floats * 4,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
  }

 prepare(device: GPUDevice, camera: Camera, transform: Transform): void {
  if (!this.camera_buffer) return;

  device.queue.writeBuffer(
    this.camera_buffer,
    0,
    camera.view_projection_matrix.buffer as ArrayBuffer,
    camera.view_projection_matrix.byteOffset,
    camera.view_projection_matrix.byteLength
  );

  const verts: number[] = [];
  const volumes = this.collision_service.getAll();

  for (const volume of volumes) {
    const color = this.color_for_volume(volume);

    const aabb = volume.getWorldAabb();
    if (aabb) {
      this.append_aabb_lines(verts, aabb, color);
      continue;
    }

    const sphere = volume.getWorldSphere();
    if (sphere) {
      this.append_sphere_lines(verts, sphere, color, 24);
    }
  }

  const data = new Float32Array(verts);
  this.vertex_count = data.length / 7;

  if (data.length === 0) return;

  this.ensure_vertex_capacity(device, data.length);
  if (!this.vertex_buffer) return;

  device.queue.writeBuffer(
    this.vertex_buffer,
    0,
    data.buffer,
    data.byteOffset,
    data.byteLength
  );
}


  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.bind_group || !this.vertex_buffer) return;
    if (this.vertex_count <= 0) return;

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind_group);
    pass.setVertexBuffer(0, this.vertex_buffer);
    pass.draw(this.vertex_count);
  }

  private color_for_volume(volume: CollisionVolume): [number, number, number, number] {
    if (volume.isTrigger) return [1.0, 0.8, 0.2, 1.0];
    if (volume.layer === "nav") return [0.2, 1.0, 1.0, 1.0];
    return [1.0, 0.25, 0.25, 1.0];
  }

  private push_line(
    out: number[],
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    r: number, g: number, b: number, a: number
  ): void {
    out.push(ax, ay, az, r, g, b, a);
    out.push(bx, by, bz, r, g, b, a);
  }

  private append_aabb_lines(
    out: number[],
    aabb: Aabb,
    color: [number, number, number, number]
  ): void {
    const min = aabb.min;
    const max = aabb.max;
    const [r, g, b, a] = color;

    const p000: [number, number, number] = [min[0], min[1], min[2]];
    const p100: [number, number, number] = [max[0], min[1], min[2]];
    const p110: [number, number, number] = [max[0], max[1], min[2]];
    const p010: [number, number, number] = [min[0], max[1], min[2]];

    const p001: [number, number, number] = [min[0], min[1], max[2]];
    const p101: [number, number, number] = [max[0], min[1], max[2]];
    const p111: [number, number, number] = [max[0], max[1], max[2]];
    const p011: [number, number, number] = [min[0], max[1], max[2]];

    this.push_line(out, ...p000, ...p100, r, g, b, a);
    this.push_line(out, ...p100, ...p110, r, g, b, a);
    this.push_line(out, ...p110, ...p010, r, g, b, a);
    this.push_line(out, ...p010, ...p000, r, g, b, a);

    this.push_line(out, ...p001, ...p101, r, g, b, a);
    this.push_line(out, ...p101, ...p111, r, g, b, a);
    this.push_line(out, ...p111, ...p011, r, g, b, a);
    this.push_line(out, ...p011, ...p001, r, g, b, a);

    this.push_line(out, ...p000, ...p001, r, g, b, a);
    this.push_line(out, ...p100, ...p101, r, g, b, a);
    this.push_line(out, ...p110, ...p111, r, g, b, a);
    this.push_line(out, ...p010, ...p011, r, g, b, a);
  }

  private append_sphere_lines(
    out: number[],
    sphere: BoundingSphere,
    color: [number, number, number, number],
    segments: number
  ): void {
    this.append_circle_xy(out, sphere.center, sphere.radius, color, segments);
    this.append_circle_xz(out, sphere.center, sphere.radius, color, segments);
    this.append_circle_yz(out, sphere.center, sphere.radius, color, segments);
  }

  private append_circle_xy(
    out: number[],
    c: Float32Array,
    radius: number,
    color: [number, number, number, number],
    segments: number
  ): void {
    const [r, g, b, a] = color;

    for (let i = 0; i < segments; i++) {
      const a0 = (i / segments) * Math.PI * 2;
      const a1 = ((i + 1) / segments) * Math.PI * 2;

      const x0 = c[0] + Math.cos(a0) * radius;
      const y0 = c[1] + Math.sin(a0) * radius;
      const z0 = c[2];

      const x1 = c[0] + Math.cos(a1) * radius;
      const y1 = c[1] + Math.sin(a1) * radius;
      const z1 = c[2];

      this.push_line(out, x0, y0, z0, x1, y1, z1, r, g, b, a);
    }
  }

  private append_circle_xz(
    out: number[],
    c: Float32Array,
    radius: number,
    color: [number, number, number, number],
    segments: number
  ): void {
    const [r, g, b, a] = color;

    for (let i = 0; i < segments; i++) {
      const a0 = (i / segments) * Math.PI * 2;
      const a1 = ((i + 1) / segments) * Math.PI * 2;

      const x0 = c[0] + Math.cos(a0) * radius;
      const y0 = c[1];
      const z0 = c[2] + Math.sin(a0) * radius;

      const x1 = c[0] + Math.cos(a1) * radius;
      const y1 = c[1];
      const z1 = c[2] + Math.sin(a1) * radius;

      this.push_line(out, x0, y0, z0, x1, y1, z1, r, g, b, a);
    }
  }

  private append_circle_yz(
    out: number[],
    c: Float32Array,
    radius: number,
    color: [number, number, number, number],
    segments: number
  ): void {
    const [r, g, b, a] = color;

    for (let i = 0; i < segments; i++) {
      const a0 = (i / segments) * Math.PI * 2;
      const a1 = ((i + 1) / segments) * Math.PI * 2;

      const x0 = c[0];
      const y0 = c[1] + Math.cos(a0) * radius;
      const z0 = c[2] + Math.sin(a0) * radius;

      const x1 = c[0];
      const y1 = c[1] + Math.cos(a1) * radius;
      const z1 = c[2] + Math.sin(a1) * radius;

      this.push_line(out, x0, y0, z0, x1, y1, z1, r, g, b, a);
    }
  }
}